/**
 * Engood Wishlist — Product Card Integration (C1 rewrite)
 *
 * Storage strategy:
 *  - Logged-in customers: key = "engood-wishlist-{customerId}"
 *    → Wishlist is isolated per customer even on shared devices.
 *    → Data persists across sessions on the same browser.
 *  - Guests: key = "engood-wishlist-guest"
 *    → Ephemeral on incognito, persistent on a regular browser.
 *    → A "Sign in to save your wishlist" banner is shown once per session.
 *
 * When a guest signs in, their guest wishlist is merged into their
 * customer-scoped wishlist (one-time migration on login).
 */

(function () {
    const GUEST_KEY = 'engood-wishlist-guest';
    const SIGN_IN_PROMPT_KEY = 'engood-wishlist-signin-prompted';

    // ─── Helpers ──────────────────────────────────────────────────────────────

    function getStorageKey() {
        const customer = window.EngoodCustomer;
        if (customer && customer.loggedIn && customer.id) {
            return `engood-wishlist-${customer.id}`;
        }
        return GUEST_KEY;
    }

    function isStorageAvailable() {
        try {
            const testKey = '__engood_test__';
            localStorage.setItem(testKey, '1');
            localStorage.removeItem(testKey);
            return true;
        } catch (e) {
            return false;
        }
    }

    const storageAvailable = isStorageAvailable();

    function getWishlist() {
        if (!storageAvailable) return [];
        try {
            return JSON.parse(localStorage.getItem(getStorageKey())) || [];
        } catch (e) {
            return [];
        }
    }

    function saveWishlist(wishlist) {
        if (!storageAvailable) return;
        localStorage.setItem(getStorageKey(), JSON.stringify(wishlist));
        window.dispatchEvent(new CustomEvent('wishlist-updated', { detail: { wishlist } }));
    }

    function isInWishlist(handle) {
        return getWishlist().some((item) => item.handle === handle);
    }

    function updateButtonUI(btn, isActive) {
        const icon = btn.querySelector('.wishlist-icon');
        if (isActive) {
            icon.classList.add('text-red-500');
            icon.classList.remove('text-gray-600');
            btn.setAttribute('aria-label', 'Remove from wishlist');
        } else {
            icon.classList.remove('text-red-500');
            icon.classList.add('text-gray-600');
            btn.setAttribute('aria-label', 'Add to wishlist');
        }
    }

    // ─── Guest sign-in prompt ─────────────────────────────────────────────────

    function showSignInPrompt() {
        // Show once per browser session
        if (sessionStorage.getItem(SIGN_IN_PROMPT_KEY)) return;
        sessionStorage.setItem(SIGN_IN_PROMPT_KEY, '1');

        const customer = window.EngoodCustomer;
        const loginUrl = (customer && customer.loginUrl) || '/account/login';

        const banner = document.createElement('div');
        banner.id = 'wishlist-signin-banner';
        banner.setAttribute('role', 'status');
        banner.className = [
            'fixed bottom-6 left-1/2 -translate-x-1/2',
            'flex items-center gap-3',
            'bg-gray-900 text-white text-sm font-medium',
            'px-4 py-3 rounded-xl shadow-2xl z-[300]',
            'opacity-0 translate-y-4 transition-all duration-300',
            'w-[calc(100vw-2rem)] max-w-sm',
        ].join(' ');

        banner.innerHTML = `
            <span class="material-symbols-outlined text-red-400 shrink-0">favorite</span>
            <span class="flex-1">Sign in to keep your wishlist safe</span>
            <a
                href="${loginUrl}"
                class="shrink-0 bg-primary text-white font-bold text-xs px-3 py-1.5 rounded-lg hover:bg-[#1a6b1a] transition-colors"
            >Sign in</a>
            <button
                id="wishlist-signin-dismiss"
                class="shrink-0 text-gray-400 hover:text-white transition-colors"
                aria-label="Dismiss"
            >
                <span class="material-symbols-outlined text-[18px]">close</span>
            </button>
        `;

        document.body.appendChild(banner);

        // Animate in
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                banner.classList.remove('opacity-0', 'translate-y-4');
            });
        });

        // Dismiss button
        document.getElementById('wishlist-signin-dismiss')?.addEventListener('click', () => {
            banner.classList.add('opacity-0', 'translate-y-4');
            setTimeout(() => banner.remove(), 300);
        });

        // Auto-dismiss after 8 seconds
        setTimeout(() => {
            if (banner.isConnected) {
                banner.classList.add('opacity-0', 'translate-y-4');
                setTimeout(() => banner.remove(), 300);
            }
        }, 8000);
    }

    // ─── Guest → Customer wishlist migration on login ─────────────────────────
    // If a logged-in customer has an existing guest wishlist, merge it into their
    // customer-scoped wishlist (runs once, then cleans up the guest key).

    function migrateGuestWishlist() {
        const customer = window.EngoodCustomer;
        if (!customer || !customer.loggedIn || !customer.id || !storageAvailable) return;

        const guestWishlist = (() => {
            try { return JSON.parse(localStorage.getItem(GUEST_KEY)) || []; } catch { return []; }
        })();

        if (guestWishlist.length === 0) return;

        const customerKey = `engood-wishlist-${customer.id}`;
        const customerWishlist = (() => {
            try { return JSON.parse(localStorage.getItem(customerKey)) || []; } catch { return []; }
        })();

        // Merge: customer items take precedence; add any guest items not already present
        const mergedHandles = new Set(customerWishlist.map((i) => i.handle));
        const itemsToAdd = guestWishlist.filter((i) => !mergedHandles.has(i.handle));

        if (itemsToAdd.length > 0) {
            const merged = [...customerWishlist, ...itemsToAdd];
            localStorage.setItem(customerKey, JSON.stringify(merged));
            console.log(`[Wishlist] Migrated ${itemsToAdd.length} guest item(s) to customer wishlist.`);
        }

        // Always clean up the guest key after migration
        localStorage.removeItem(GUEST_KEY);
    }

    // ─── Button initialization ────────────────────────────────────────────────

    function initWishlistButtons() {
        document.querySelectorAll('.wishlist-btn').forEach((btn) => {
            if (btn.dataset.initialized) return;
            btn.dataset.initialized = 'true';

            const handle = btn.dataset.productHandle;

            // Set initial state
            updateButtonUI(btn, isInWishlist(handle));

            // Add click handler
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                let wishlist = getWishlist();
                const index = wishlist.findIndex((item) => item.handle === handle);

                if (index > -1) {
                    // Remove from wishlist
                    wishlist.splice(index, 1);
                    updateButtonUI(btn, false);
                } else {
                    // Add to wishlist
                    wishlist.push({
                        handle: btn.dataset.productHandle,
                        title: btn.dataset.productTitle,
                        url: btn.dataset.productUrl,
                        image: btn.dataset.productImage,
                        price: btn.dataset.productPrice,
                        variantId: btn.dataset.variantId,
                        hasOnlyDefaultVariant: btn.dataset.hasOnlyDefaultVariant === 'true',
                    });
                    updateButtonUI(btn, true);

                    // Show sign-in prompt for guests
                    const customer = window.EngoodCustomer;
                    if (!customer || !customer.loggedIn) {
                        showSignInPrompt();
                    }
                }

                saveWishlist(wishlist);
            });
        });
    }

    // ─── Sync all button states when wishlist changes ─────────────────────────

    function syncAllButtonStates() {
        document.querySelectorAll('.wishlist-btn').forEach((btn) => {
            const handle = btn.dataset.productHandle;
            updateButtonUI(btn, isInWishlist(handle));
        });
    }

    // ─── Update Header Icons (Option A) ───────────────────────────────────────

    function updateWishlistBadges() {
        const count = getWishlist().length;
        document.querySelectorAll('.header-wishlist-icon').forEach((icon) => {
            if (count > 0) {
                icon.textContent = 'favorite';
                icon.classList.add('text-red-500');
            } else {
                icon.textContent = 'favorite_border';
                icon.classList.remove('text-red-500');
            }
        });
    }

    // ─── Boot ─────────────────────────────────────────────────────────────────

    // Run migration first (before init, so merged state is visible immediately)
    migrateGuestWishlist();

    // Init buttons & badges
    function init() {
        initWishlistButtons();
        updateWishlistBadges();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-init and sync when AJAX content is loaded (e.g., collection filters)
    window.addEventListener('wishlist-updated', () => {
        syncAllButtonStates();
        updateWishlistBadges();
    });

    window.addEventListener('wishlist-signin-prompt', () => {
        showSignInPrompt();
    });

    // Expose the storage key resolver globally so wishlist-template.js can use it
    window.EngoodWishlist = {
        getStorageKey,
        getWishlist,
        saveWishlist,
        isStorageAvailable: () => storageAvailable,
        showSignInPrompt
    };
})();